// Lead Lists — list + CSV/XLSX import + row preview.
import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Upload, Loader2, FileSpreadsheet, ChevronRight, Download } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export default function LeadListsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [outcomes, setOutcomes] = useState(null); // {total, outcomes:[], pipeline_states:[], max_attempts}
  const [selStatuses, setSelStatuses] = useState({});
  const [rechurning, setRechurning] = useState(false);
  const [resetAttempts, setResetAttempts] = useState(false);
  const [mapping, setMapping] = useState(null); // {headers, sample_rows, staging_file, list_id, campaigns, campaign_id, screen, column_map}
  const [confirming, setConfirming] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [loadingScreen, setLoadingScreen] = useState(false);
  const { canWrite, canDelete } = useCurrentModulePerms();

  async function load() {
    setLoading(true);
    try { const r = await api.get("/admin/resources/lead_lists"); setRows(r.data || []); }
    catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  useEffect(() => {
    load();
    api.get("/admin/resources/campaigns")
      .then((r) => setCampaigns(r.data || []))
      .catch(() => setCampaigns([]));
  }, []);

  function startNew() { setForm({ name: "", description: "", is_active: true, dedup_key: "phone", tz_aware: true, campaign_id: "", max_attempts: "" }); setEditing("new"); }
  function startEdit(r) { setForm({ name: r.name, description: r.description||"", is_active: !!r.is_active, dedup_key: r.data?.dedup_key||"phone", tz_aware: !!r.data?.tz_aware, campaign_id: r.data?.campaign_id||"", max_attempts: (r.data?.max_attempts ?? "") }); setEditing(r); }

  const campaignName = (id) => campaigns.find((c) => c.id === id)?.name || null;
  const campaignById = (id) => campaigns.find((c) => c.id === id) || null;

  async function save() {
    if (!form.name?.trim()) { toast.error("Name required"); return; }
    if (!form.campaign_id) { toast.error("Select a campaign for this list"); return; }
    setSaving(true);
    try {
      const prevData = (editing !== "new" && editing?.data) ? editing.data : {};
      const maxAtt = (form.max_attempts === "" || form.max_attempts == null) ? null : Number(form.max_attempts);
      const payload = { name: form.name.trim(), description: form.description, is_active: !!form.is_active, data: { ...prevData, dedup_key: form.dedup_key, tz_aware: !!form.tz_aware, campaign_id: form.campaign_id, max_attempts: maxAtt, source: prevData.source || "manual" } };
      if (editing === "new") await api.post("/admin/resources/lead_lists", payload);
      else await api.patch(`/admin/resources/lead_lists/${editing.id}`, payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  }
  async function remove(r) {
    if (!confirm(`Delete "${r.name}" and ALL its rows?`)) return;
    try { await api.delete(`/admin/resources/lead_lists/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  }

  async function importCSV(listId, file) {
    if (!file) return;
    const list = rows.find((x) => x.id === listId);
    const campId = list?.data?.campaign_id;
    if (!campId) { toast.error("Map this list to a campaign first (edit the list), then upload."); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(`/admin/lead-lists/${listId}/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMapping({
        list_id: listId,
        headers: r.data.headers || [],
        sample_rows: r.data.sample_rows || [],
        staging_file: r.data.staging_file,
        campaign_id: campId,
        campaign_name: campaignName(campId),
        screen: null,
        column_map: {},
        include_extra: false,
      });
      resolveScreen(campId, r.data.headers || [], list);
    } catch (e) { toast.error(formatErr(e)); }
  }

  async function downloadTemplate(r) {
    try {
      const resp = await api.get(`/admin/lead-lists/${r.id}/template`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(r.name || "leads").replace(/[^A-Za-z0-9._-]+/g, "_")}_template.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (e) { toast.error(formatErr(e)); }
  }

  // Normalize a header/label so "First Name" ≈ "first_name" ≈ "firstname".
  function norm(s) { return (s || "").toLowerCase().replace(/[^a-z0-9]/g, ""); }

  function autoMap(fields, headers) {
    const map = {};
    (fields || []).forEach((f) => {
      const target = [norm(f.key), norm(f.label), norm(f.binding)];
      const hit = (headers || []).find((h) => target.includes(norm(h)));
      if (hit) map[f.key] = hit;
    });
    return map;
  }

  async function resolveScreen(campaignId, headers, list) {
    setLoadingScreen(true);
    try {
      const camp = campaignById(campaignId);
      const isBroadcast = (camp?.data?.mode || "") === "voice_broadcast";
      let screen;
      if (isBroadcast) {
        // Voice broadcast: no CRM screen — minimal Phone + First name.
        screen = { id: null, name: "Voice Broadcast (minimal)", broadcast: true, fields: [
          { key: "_phone", label: "Phone number", type: "phone", binding: "phone", required: true, synthetic: true },
          { key: "first_name", label: "First name", type: "text", binding: "first_name" },
        ] };
      } else {
        const r = await api.get("/admin/crm-screen-resolve", { params: { campaign_id: campaignId || undefined } });
        screen = r.data;
        const flds = [...(screen.fields || [])];
        if (!flds.some((f) => f.binding === "phone")) {
          flds.unshift({ key: "_phone", label: "Phone number", type: "phone", binding: "phone", required: true, synthetic: true });
        }
        screen.fields = flds;
      }
      const hdrs = headers || mapping?.headers || [];
      setMapping((m) => m && ({ ...m, campaign_id: campaignId, screen, column_map: autoMap(screen.fields || [], hdrs) }));
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoadingScreen(false); }
  }

  async function confirmImport() {
    if (!mapping) return;
    const fields = mapping.screen?.fields || [];
    const phoneField = fields.find((f) => f.binding === "phone");
    if (phoneField && !mapping.column_map[phoneField.key]) {
      toast.error("Map the Phone field before importing."); return;
    }
    setConfirming(true);
    try {
      const r = await api.post(`/admin/lead-lists/${mapping.list_id}/confirm-import`, {
        staging_file: mapping.staging_file,
        column_map: mapping.column_map,
        crm_screen_id: mapping.screen?.id || undefined,
        campaign_id: mapping.campaign_id || undefined,
        include_extra: !!mapping.include_extra,
      });
      toast.success(`Imported ${r.data.inserted} new (${r.data.duplicates_skipped} dups). Total: ${r.data.total}`);
      setMapping(null); load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setConfirming(false); }
  }

  async function openView(r) {
    setViewing(r);
    setOutcomes(null); setSelStatuses({});
    try { const x = await api.get(`/admin/lead-lists/${r.id}/rows`); setPreviewRows(x.data?.rows || []); }
    catch (e) { setPreviewRows([]); }
    loadOutcomes(r.id);
  }

  async function loadOutcomes(listId) {
    try { const o = await api.get(`/admin/lead-lists/${listId}/outcomes`); setOutcomes(o.data || null); }
    catch { setOutcomes(null); }
  }

  async function rechurn() {
    if (!viewing) return;
    const statuses = Object.keys(selStatuses).filter((k) => selStatuses[k]);
    if (statuses.length === 0) { toast.error("Select at least one disposition to re-churn"); return; }
    const totalSel = (outcomes?.outcomes || []).filter((o) => statuses.includes(o.status)).reduce((a, o) => a + o.count, 0);
    const attemptsNote = resetAttempts ? "attempt counters reset to 0" : "attempts kept, capped by this list's max attempts";
    if (!confirm(`Re-churn ${totalSel} lead(s) across ${statuses.length} disposition(s)? They will be re-dialed (${attemptsNote}).`)) return;
    setRechurning(true);
    try {
      const r = await api.post(`/admin/lead-lists/${viewing.id}/rechurn`, { statuses, reset_attempts: resetAttempts });
      toast.success(`Re-churned ${r.data?.reset_count || 0} lead(s) — back in the dial pool`);
      if (r.data?.at_max_attempts > 0) {
        toast.warning(`${r.data.at_max_attempts} lead(s) already reached max attempts and stay in "max_attempts". Tick it with "Reset attempts" to re-dial them, or raise Max attempts.`);
      }
      setSelStatuses({});
      setResetAttempts(false);
      const x = await api.get(`/admin/lead-lists/${viewing.id}/rows`); setPreviewRows(x.data?.rows || []);
      loadOutcomes(viewing.id);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setRechurning(false); }
  }

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="leadlists-title">Lead Lists</h1>
          <p className="text-sm text-muted-foreground mt-1">Upload customer numbers via CSV/Excel. Reference these lists from campaigns.</p>
        </div>
        {canWrite && <Button data-testid="add-leadlist-btn" onClick={startNew} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"><Plus className="w-4 h-4"/> New list</Button>}
      </div>

      <Card className="overflow-hidden">
        {loading ? <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground"><tr className="text-left">
              <th className="px-4 py-2">Name</th><th className="px-4 py-2">Campaign</th><th className="px-4 py-2">Contacts</th><th className="px-4 py-2">Source</th><th className="px-4 py-2">Active</th><th className="px-4 py-2 w-72">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-muted/30" data-testid={`row-leadlist-${r.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2"><FileSpreadsheet className="w-4 h-4 text-primary"/><span className="font-medium">{r.name}</span></div>
                    {r.description && <div className="text-xs text-muted-foreground ml-6">{r.description}</div>}
                  </td>
                  <td className="px-4 py-2.5" data-testid={`ll-campaign-${r.id}`}>
                    {r.data?.campaign_id
                      ? <Badge variant="secondary" className="text-[11px] max-w-[180px] truncate">{campaignName(r.data.campaign_id) || "—"}</Badge>
                      : <span className="text-xs text-amber-600">Unmapped</span>}
                  </td>
                  <td className="px-4 py-2.5"><Badge variant="outline">{r.data?.contacts_count ?? 0}</Badge></td>
                  <td className="px-4 py-2.5 text-xs">{r.data?.source || "manual"}</td>
                  <td className="px-4 py-2.5">{r.is_active ? <Badge variant="outline" className="text-[10px]">Active</Badge> : <Badge variant="destructive" className="text-[10px]">Off</Badge>}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1">
                      <Button data-testid={`view-leadlist-${r.id}`} variant="ghost" size="sm" className="h-8 gap-1" onClick={()=>openView(r)}>View <ChevronRight className="w-3 h-3"/></Button>
                      <Button data-testid={`template-leadlist-${r.id}`} variant="ghost" size="sm" className="h-8 gap-1" onClick={()=>downloadTemplate(r)} title="Download the CSV format this list expects"><Download className="w-3.5 h-3.5"/> Template</Button>
                      <label className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-border text-sm hover:bg-muted cursor-pointer" data-testid={`import-leadlist-${r.id}`}>
                        <Upload className="w-3.5 h-3.5"/> Import CSV
                        <input type="file" accept=".csv,.xlsx,.xls,.txt" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) importCSV(r.id, f); e.target.value=""; }} />
                      </label>
                      {canWrite && <Button data-testid={`edit-leadlist-${r.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={()=>startEdit(r)}><Edit2 className="w-4 h-4"/></Button>}
                      {canDelete && <Button data-testid={`del-leadlist-${r.id}`} variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={()=>remove(r)}><Trash2 className="w-4 h-4"/></Button>}
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-sm text-muted-foreground">No lead lists yet.</td></tr>}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={!!editing} onOpenChange={(v)=>!v&&setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing==="new"?"New lead list":`Edit ${editing?.name}`}</DialogTitle><DialogDescription>After creating, click "Import CSV" on the row to upload numbers.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Name</Label><Input data-testid="ll-name" value={form.name||""} onChange={(e)=>setForm({...form, name:e.target.value})} /></div>
            <div><Label>Description</Label><Input data-testid="ll-desc" value={form.description||""} onChange={(e)=>setForm({...form, description:e.target.value})} /></div>
            <div>
              <Label>Campaign <span className="text-destructive">*</span></Label>
              <select data-testid="ll-campaign" value={form.campaign_id||""} onChange={(e)=>setForm({...form, campaign_id:e.target.value})}
                className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm">
                <option value="">— select a campaign —</option>
                {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}{c.data?.mode==="voice_broadcast"?" (Voice Broadcast)":""}</option>)}
              </select>
              <p className="text-[11px] text-muted-foreground mt-1">Leads uploaded to this list feed this campaign. A campaign can have multiple lists.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Dedup key</Label><Input data-testid="ll-dedup" value={form.dedup_key||"phone"} onChange={(e)=>setForm({...form, dedup_key:e.target.value})} /></div>
              <div><Label>Max attempts</Label><Input data-testid="ll-max-attempts" type="number" min={1} placeholder="Campaign default" value={form.max_attempts ?? ""} onChange={(e)=>setForm({...form, max_attempts:e.target.value})} /><p className="text-[11px] text-muted-foreground mt-1">Max times a lead is dialed. Blank = use campaign default.</p></div>
            </div>
            <div className="flex items-center gap-2"><Switch data-testid="ll-tz" checked={!!form.tz_aware} onCheckedChange={(v)=>setForm({...form, tz_aware:!!v})} /><Label>Time-zone aware</Label></div>
            <div className="flex items-center gap-2"><Switch data-testid="ll-active" checked={!!form.is_active} onCheckedChange={(v)=>setForm({...form, is_active:!!v})} /><Label>Active</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setEditing(null)}>Cancel</Button><Button data-testid="ll-save" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground">{saving?<Loader2 className="w-4 h-4 animate-spin"/>:"Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewing} onOpenChange={(v)=>!v&&setViewing(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <DialogTitle>{viewing?.name} — first 100 rows</DialogTitle>
                <DialogDescription>Numbers loaded into this list.</DialogDescription>
              </div>
              {previewRows.some(r => r.status === "dialing") && (
                <Button
                  data-testid="reset-stuck-btn"
                  size="sm"
                  variant="outline"
                  className="text-amber-600 border-amber-300 hover:bg-amber-50"
                  onClick={async () => {
                    if (!viewing) return;
                    try {
                      const r = await api.post(`/admin/lead-lists/${viewing.id}/reset-stuck`);
                      toast.success(`Reset ${r.data?.reset_count || 0} stuck lead(s)`);
                      const x = await api.get(`/admin/lead-lists/${viewing.id}/rows`);
                      setPreviewRows(x.data?.rows || []);
                    } catch (e) { toast.error(formatErr(e)); }
                  }}
                >
                  Reset stuck dialing
                </Button>
              )}
            </div>
          </DialogHeader>
          {outcomes && (outcomes.outcomes?.length > 0 || outcomes.pipeline_states?.length > 0) && (
            <div className="rounded-md border border-border bg-muted/20 p-3 mb-2" data-testid="rechurn-panel">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div>
                  <div className="text-sm font-semibold">Outcomes — re-churn</div>
                  <div className="text-[11px] text-muted-foreground" data-testid="rechurn-attempts-note">Tick dispositions to re-dial. {resetAttempts ? "Attempt counters will be reset to 0" : "Attempts are kept"}{(outcomes.effective_max_attempts || outcomes.max_attempts) ? ` (max ${outcomes.effective_max_attempts || outcomes.max_attempts})` : ""}.</div>
                </div>
                <div className="flex items-center gap-3">
                <label data-testid="rechurn-reset-attempts" className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
                  <input type="checkbox" className="accent-current" checked={resetAttempts}
                    data-testid="rechurn-reset-attempts-checkbox"
                    onChange={(e)=>setResetAttempts(e.target.checked)} />
                  Reset attempts
                </label>
                <Button data-testid="rechurn-btn" size="sm" onClick={rechurn}
                  disabled={rechurning || Object.values(selStatuses).every((v)=>!v)}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  {rechurning ? <Loader2 className="w-4 h-4 animate-spin"/> : "Re-churn selected"}
                </Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {(outcomes.outcomes || []).map((o) => (
                  <label key={o.status} data-testid={`rechurn-opt-${o.status}`}
                    className={`inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs cursor-pointer ${selStatuses[o.status] ? "border-primary bg-primary/10" : "border-border hover:bg-muted"}`}>
                    <input type="checkbox" className="accent-current"
                      checked={!!selStatuses[o.status]}
                      onChange={(e)=>setSelStatuses({...selStatuses, [o.status]: e.target.checked})} />
                    <span className="font-medium">{o.status}</span>
                    <Badge variant="secondary" className="text-[10px]">{o.count}</Badge>
                  </label>
                ))}
                {(outcomes.outcomes || []).length === 0 && (
                  <span className="text-xs text-muted-foreground">No dispositioned outcomes yet.</span>
                )}
              </div>
              {(outcomes.pipeline_states || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-border/60">
                  {(outcomes.pipeline_states || []).map((o) => (
                    <span key={o.status} className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                      {o.status}: <span className="font-medium text-foreground">{o.count}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground"><tr className="text-left"><th className="px-3 py-2">Phone</th><th className="px-3 py-2">Name</th><th className="px-3 py-2">Status</th></tr></thead>
            <tbody>
              {previewRows.map((x) => <tr key={x.id} className="border-t" data-testid={`leadrow-${x.id}`}><td className="px-3 py-2 font-mono text-xs">{x.phone}</td><td className="px-3 py-2">{x.name||"—"}</td><td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{x.status}</Badge></td></tr>)}
              {previewRows.length === 0 && <tr><td colSpan={3} className="text-center py-8 text-sm text-muted-foreground">No rows. Click "Import CSV" to upload.</td></tr>}
            </tbody>
          </table>
        </DialogContent>
      </Dialog>

      {/* Column mapping preview before final import */}
      <Dialog open={!!mapping} onOpenChange={(v)=>!v&&setMapping(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Map CSV columns to campaign fields</DialogTitle>
            <DialogDescription>Pick the campaign whose CRM screen defines the lead fields, then match each field to a column from your file. These values pop on the agent's screen when the lead connects.</DialogDescription>
          </DialogHeader>
          {mapping && (
            <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-4 py-2">
              <div className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2">
                <div>
                  <div className="text-[11px] text-muted-foreground">Campaign</div>
                  <div className="font-medium text-sm" data-testid="map-campaign-name">{mapping.campaign_name || "—"}</div>
                </div>
                {mapping.screen && (
                  <div className="text-right">
                    <div className="text-[11px] text-muted-foreground">Screen</div>
                    <div className="text-sm font-medium">{mapping.screen.name} · {(mapping.screen.fields||[]).length} field(s)</div>
                  </div>
                )}
              </div>

              {loadingScreen ? (
                <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto"/></div>
              ) : mapping.screen ? (
                <div className="border rounded-md overflow-auto max-h-[42vh]">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-muted-foreground sticky top-0 z-10">
                      <tr className="text-left">
                        <th className="px-3 py-2">CRM field</th>
                        <th className="px-3 py-2">Type</th>
                        <th className="px-3 py-2 w-56">CSV column</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(mapping.screen.fields || []).map((f) => (
                        <tr key={f.key} className="border-t" data-testid={`map-field-${f.key}`}>
                          <td className="px-3 py-2">
                            <div className="font-medium flex items-center gap-1.5">
                              {f.label}
                              {f.binding === "phone" && <Badge className="bg-primary text-primary-foreground text-[9px]">PHONE</Badge>}
                              {f.required && <Badge variant="outline" className="text-[9px] border-rose-300 text-rose-600">req</Badge>}
                            </div>
                            <code className="text-[10px] text-muted-foreground">{f.key}</code>
                          </td>
                          <td className="px-3 py-2"><Badge variant="secondary" className="text-[10px] uppercase">{f.type}</Badge></td>
                          <td className="px-3 py-2">
                            <select
                              data-testid={`map-col-${f.key}`}
                              value={mapping.column_map[f.key] || ""}
                              onChange={(e)=>setMapping({...mapping, column_map: {...mapping.column_map, [f.key]: e.target.value}})}
                              className={`w-full h-8 rounded-md border bg-background px-2 text-xs ${f.binding==="phone" && !mapping.column_map[f.key] ? "border-rose-400" : "border-border"}`}
                            >
                              <option value="">— not mapped —</option>
                              {mapping.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                            </select>
                          </td>
                        </tr>
                      ))}
                      {(mapping.screen.fields || []).length === 0 && (
                        <tr><td colSpan={3} className="text-center py-6 text-sm text-muted-foreground">This CRM screen has no fields. Add fields in Admin → CRM Screens.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : null}

              <div className="flex items-center gap-2 rounded-md border px-3 py-2">
                <Switch data-testid="map-include-extra" checked={!!mapping.include_extra} onCheckedChange={(v)=>setMapping({...mapping, include_extra: !!v})} />
                <div>
                  <Label className="text-sm">Also import all other columns as extra fields</Label>
                  <p className="text-[11px] text-muted-foreground">Stores any unmapped CSV columns on each lead (useful for voice broadcast or richer data).</p>
                </div>
              </div>

              <div className="border-t pt-3">
                <div className="text-overline text-muted-foreground mb-2">File preview (first 5 rows)</div>
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 text-muted-foreground">
                      <tr>
                        {mapping.headers.map((h) => {
                          const mappedTo = Object.entries(mapping.column_map).find(([,col]) => col===h);
                          return (
                            <th key={h} className={`px-3 py-2 text-left ${mappedTo?"bg-primary/10 text-primary":""}`}>{h}</th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {(mapping.sample_rows || []).map((row, i) => (
                        <tr key={i} className="border-t" data-testid={`map-sample-${i}`}>
                          {mapping.headers.map((h, j) => (
                            <td key={j} className="px-3 py-2">{row[j] || <span className="text-muted-foreground/40">—</span>}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Duplicates against existing rows in this list will be skipped automatically.</p>
            </div>
          )}
          <DialogFooter className="shrink-0 border-t pt-3">
            <Button variant="outline" onClick={()=>setMapping(null)}>Cancel</Button>
            <Button data-testid="map-confirm-btn" onClick={confirmImport} disabled={confirming || loadingScreen} className="bg-primary hover:bg-primary/90 text-primary-foreground">
              {confirming ? <Loader2 className="w-4 h-4 animate-spin"/> : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
